import { useState, useEffect, useCallback } from 'react';
import { ModernNotification, NotificationState } from '../types/ModernNotification';
import { notificationService } from '../services/notificationService';
import { LCName, getItemLC } from '../../localstorage';

export const useNotifications = () => {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: undefined
  });

  // Load notifications on mount and subscribe to real-time updates
  useEffect(() => {
    loadNotifications();

    // Subscribe to real-time notifications
    const unsubscribe = notificationService.subscribeToNotifications(
      (updatedNotifications) => {
        const unreadCount = notificationService.getUnreadCount(updatedNotifications);
        setState({
          notifications: updatedNotifications,
          unreadCount,
          isLoading: false,
          error: undefined
        });
      }
    );

    // Clean up subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Listen for storage events (legacy compatibility)
  useEffect(() => {
    const handleStorageChange = () => {
      loadNotifications();
    };

    window.addEventListener('storageNotif', handleStorageChange);
    return () => {
      window.removeEventListener('storageNotif', handleStorageChange);
    };
  }, []);

  const loadNotifications = useCallback(async () => {
    console.log('useNotifications: Starting to load notifications...');
    setState(prev => ({ ...prev, isLoading: true, error: undefined }));

    try {
      // Get notifications from Firebase (includes all fallback logic)
      const notifications = await notificationService.getNotifications();
      
      console.log('useNotifications: Loaded', notifications.length, 'notifications');

      // Sort by timestamp (newest first)
      notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      const unreadCount = notificationService.getUnreadCount(notifications);

      setState({
        notifications,
        unreadCount,
        isLoading: false,
        error: undefined
      });
    } catch (error) {
      console.error('useNotifications: Error loading notifications:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load notifications'
      }));
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const currentNotifications = state.notifications;
      const updatedNotifications = await notificationService.markAsRead(notificationId, currentNotifications);
      const unreadCount = notificationService.getUnreadCount(updatedNotifications);
      
      setState(prev => ({
        ...prev,
        notifications: updatedNotifications,
        unreadCount,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      console.error('Error marking notification as read:', error);
    }
  }, [state.notifications]);

  const markAllAsRead = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const currentNotifications = state.notifications;
      const updatedNotifications = await notificationService.markAllAsRead(currentNotifications);
      
      setState(prev => ({
        ...prev,
        notifications: updatedNotifications,
        unreadCount: 0,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      console.error('Error marking all notifications as read:', error);
    }
  }, [state.notifications]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const currentNotifications = state.notifications;
      const updatedNotifications = await notificationService.deleteNotification(notificationId, currentNotifications);
      const unreadCount = notificationService.getUnreadCount(updatedNotifications);
      
      setState(prev => ({
        ...prev,
        notifications: updatedNotifications,
        unreadCount,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      console.error('Error deleting notification:', error);
    }
  }, [state.notifications]);

  const deleteAllNotifications = useCallback(() => {
    setState(prev => {
      const updatedNotifications = notificationService.deleteAllNotifications();
      
      // Save to localStorage
      notificationService.saveNotifications(updatedNotifications);
      
      return {
        ...prev,
        notifications: updatedNotifications,
        unreadCount: 0
      };
    });
  }, []);

  const addNotification = useCallback((notification: Omit<ModernNotification, 'id'>) => {
    const newNotification: ModernNotification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };

    setState(prev => {
      const updatedNotifications = [newNotification, ...prev.notifications];
      const unreadCount = notificationService.getUnreadCount(updatedNotifications);
      
      // Save to localStorage
      notificationService.saveNotifications(updatedNotifications);
      
      return {
        ...prev,
        notifications: updatedNotifications,
        unreadCount
      };
    });
  }, []);

  return {
    ...state,
    actions: {
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
      addNotification,
      refresh: loadNotifications
    }
  };
};